import AnaliticaFantasyIcon from '@/components/shared/icons/analitica-fantasy-icon';
import BiwengerIcon from '@/components/shared/icons/biwenger-icon';
import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import FantasyMarcaIcon from '@/components/shared/icons/FantasyMarcaIcon';
import MisterFantasyIcon from '@/components/shared/icons/MisterFantasyIcon';
import { LEAGUES } from '@/constants/leagues';

import { FANTASY } from './biwenger-helper';
import { obtenerEquipo } from '@/data/equipos';
import { countries } from '@/utils/country';
import { getTeamImageUrl, getPlayerImageUrl, getDefaultPlayerImageUrl } from './local-images-helper';

const defaultImageSrc = `${process.env.NEXT_PUBLIC_IMAGES}/banners/analitica-fantasy.jpg`;
const playerSize = 130;
const playerHeightSize = 130;
const teamSize = 60;
const bigPlayerSize = 249;

export const playerVersion = 27;
export const teamVersion = 10;

export const getArticleImage = (articulo) => {
    if (articulo?.coverImage) {
        return articulo?.coverImage;
    }

    if (articulo?.bannerImage) {
        return articulo?.bannerImage;
    }

    return defaultImageSrc;
};

export const getPostCardImage = (cover, width, height) => {
    if (cover) {
        return `${cover}?width=${width * 1.5}&height=${height * 1.5}`;
    }

    return `${defaultImageSrc}?width=${width}&height=${height}`;
};

export const getMasterPlayerImage = (player, _, isDefault) => {
    const addWidth = `?width=${playerSize}&height=${playerHeightSize}&version=${playerVersion}`;

    if (player?.information?.photo) {
        return `${player?.information?.photo}${addWidth}`;
    }

    if (player?.photo) {
        return `${player?.photo}${addWidth}`;
    }

    if (isDefault && player.id) {
        return `${process.env.NEXT_PUBLIC_DEFAULT_IMAGES}/${player.id}.png${addWidth}`;
    }

    const id = player?.id;

    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/${id}.png${addWidth}`;
};

export const getPlayerPhotoForceId = (player) => {
    const addWidth = `?width=${playerSize}&height=${playerHeightSize}&version=${playerVersion}`;
    const id = player?.information?.id ?? player?.id;
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/${id}.png${addWidth}`;
};

export const getFantasyPlayerImage = (id, size) => {
    const addWidth = `?width=${size ?? playerSize}&height=${size ?? playerHeightSize}&version=${playerVersion}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores-fantasy/${id}.png${addWidth}`;
};

export const getMasterPhotoById = (id, size) => {
    const widthToGet = Math.round(size ?? playerSize);

    // Usar helper local que decide entre local o CDN según la flag
    return getPlayerImageUrl(id, widthToGet, widthToGet, playerVersion) ||
        `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/${id}.png?width=${widthToGet}&height=${widthToGet}&version=${playerVersion}`;
};

export const getImagePresident = (image, size) => {
    const addWidth = `?width=${size ?? playerSize}&height=${size ?? playerHeightSize}`;
    const url = `${process.env.NEXT_PUBLIC_IMAGES}/fantasy-kaos/presidentes/${image}${addWidth}`;
    return url;
};

export const getImagePresidentLogos = (image, size) => {
    const addWidth = `?width=${size ?? playerSize}&height=${size ?? playerHeightSize}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/fantasy-kaos/logos/${image}${addWidth}`;
};

/**
 * **getMasterBigPhotoById**: Genera la URL de una imagen de gran tamaño de un jugador a partir de su ID y un ancho opcional.
 *
 * @param {number|string} id - El identificador único del jugador.
 *                             Se utiliza para obtener la imagen correspondiente.
 * @param {number} [width=bigPlayerSize] - El ancho deseado para la imagen (en píxeles).
 *                                        Si no se proporciona, se utiliza un valor por defecto.
 *
 * @returns {string} - Devuelve una URL completa de la imagen del jugador,
 *                     con parámetros para ajustar el ancho y la altura.
 */
export const getMasterBigPhotoById = (id, width = bigPlayerSize) => {
    const addWidth = `?width=${width}&height=${width}&version=${playerVersion}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/${id}.png${addWidth}`;
};

export const getJugadoresSeleccion = (id, size) => {
    const addWidth = `?width=${size ?? playerSize}&height=${size ?? playerHeightSize}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores-seleccion/${id}.png${addWidth}`;
};

export const getTeamImageById = (id, size, height) => {
    const widthToGet = Math.round(size ?? teamSize);
    const heightToGet = Math.round(height ?? size ?? teamSize);

    // Usar helper local que decide entre local o CDN según la flag
    return getTeamImageUrl(id, widthToGet, heightToGet, teamVersion) ||
        `${process.env.NEXT_PUBLIC_IMAGES}/equipos/${id}.png?width=${widthToGet}&height=${heightToGet}&version=${teamVersion}`;
};

export const getTeamImageHdById = (id, size) => {
    const sizeToGet = Math.round(size ?? teamSize);

    const addWidth = `?width=${sizeToGet}&height=${sizeToGet}&version=${teamVersion}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/escudos-hd/${id}.png${addWidth}`;
};

export const getApiFootballTeamImageById = (id) => {
    const addWidth = `?width=${playerSize}&height=${playerHeightSize}`;
    return `${process.env.NEXT_PUBLIC_DEFAULT_IMAGES}/teams/${id}.png${addWidth}`;
};

export const getApiFootballPlayerImage = (id) => {
    const url = `${process.env.NEXT_PUBLIC_DEFAULT_IMAGES}/players/${id}.png`;
    return url;
};

export const getTeamCamisetaImageById = (id) => {
    const addWidth = `?width=${playerSize}&height=${playerHeightSize}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/camisetas/${id}.png${addWidth}`;
};

export const getImagenEntrenador = (id, size) => {
    const addWidth = `?width=${size ?? playerSize}&height=${size ?? playerHeightSize}`;
    return `${process.env.NEXT_PUBLIC_IMAGES}/entrenadores/${id}.png${addWidth}`;
};

export const getFantasyImage = (fantasy, width) => {
    switch (fantasy) {
        case FANTASY.BIWENGER:
            return <BiwengerIcon width={width} />;
        case FANTASY.FANTASY:
            return <FantasyLaLigaIcon iconWidth={width} />;
        case FANTASY.FANTASY_MARCA:
            return <FantasyMarcaIcon width={width} />;
        case FANTASY.MISTER_FANTASY:
            return <MisterFantasyIcon width={width} />;
        case FANTASY.ANALITICA:
            return <AnaliticaFantasyIcon width={width} />;
        default:
            return <AnaliticaFantasyIcon width={width} />;
    }
};

export const getDefaultImage = (width = 144, height = 144) => {
    // Usar helper local que decide entre local o CDN según la flag
    const localUrl = getDefaultPlayerImageUrl();
    if (localUrl && process.env.NEXT_PUBLIC_USE_LOCAL_IMAGES === 'true') {
        return localUrl;
    }
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores/0.png?width=${width}&height=${height}`;
};

export const getCountryImage = (countryCode, width) => {
    if (countryCode) {
        return `${process.env.NEXT_PUBLIC_IMAGES}/countries/${countryCode}.png?width=${width ?? 15}`;
    }

    return `${process.env.NEXT_PUBLIC_IMAGES}/competitions/${LEAGUES.FIFA_FRIENDLIES}.png?width=${width ?? 25
        }`;
};

export const getCountryImageByNationality = (nationality, width) => {
    var country = countries.find((country) => country.nationality === nationality);
    return `${process.env.NEXT_PUBLIC_IMAGES}/countries/${country?.code}.png?width=${width ?? 15}`;
};

export const shouldGetOwnImage = (teamId) => {
    const teamFound = obtenerEquipo(teamId);
    return teamFound?.id > 0;
};

export const getBiwengerImage = (id) => {
    return `${process.env.NEXT_PUBLIC_IMAGES}/jugadores-biwenger/${id}.png`;
};

export const getBalonParadoImage = (size = 60) => {
    return `${process.env.NEXT_PUBLIC_IMAGES}/assets/bp.png?width=${size}&height=${size}`;
};
